import * as React from "react";
import { useTranslation } from "react-i18next";
import {
  Calendar as CalendarIcon,
  Clock,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import TimePickerSpinner from "@/components/ui/TimePickerSpinner";
import { MonthPicker } from "@/components/ui/date-time-picker/MonthPicker";
import { YearPicker } from "@/components/ui/date-time-picker/YearPicker";
import { calendarClassNames } from "@/components/ui/date-time-picker/calendarClassNames";
import {
  DEFAULT_TIME,
  parseValue,
  useDateTimeValue,
} from "@/components/ui/date-time-picker/useDateTimeValue";

const DrilldownCtx = React.createContext(null);

function DrilldownCaption({ displayMonth }) {
  const ctx = React.useContext(DrilldownCtx);
  if (!ctx) return null;
  const { setView, setCalMonth } = ctx;

  const prevM = new Date(displayMonth.getFullYear(), displayMonth.getMonth() - 1, 1);
  const nextM = new Date(displayMonth.getFullYear(), displayMonth.getMonth() + 1, 1);
  const label = displayMonth.toLocaleDateString(undefined, { month: "long", year: "numeric" });

  return (
    <div className="flex justify-center items-center relative px-8 h-8">
      <button
        type="button"
        onClick={() => setCalMonth(prevM)}
        className="absolute left-1 h-7 w-7 bg-slate-800 hover:bg-slate-700 rounded-lg flex items-center justify-center text-slate-400 hover:text-white transition-colors"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={() => setView("months")}
        title="Click to browse by month or year"
        className="flex items-center gap-1 text-sm font-semibold text-white hover:text-[#4d8aff] px-2 py-0.5 rounded-lg hover:bg-slate-700/60 transition-colors"
      >
        {label}
        <ChevronDown className="h-3 w-3 opacity-50" />
      </button>
      <button
        type="button"
        onClick={() => setCalMonth(nextM)}
        className="absolute right-1 h-7 w-7 bg-slate-800 hover:bg-slate-700 rounded-lg flex items-center justify-center text-slate-400 hover:text-white transition-colors"
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
}

function DateTimePicker({
  value,
  onChange,
  // Sin valor por defecto aquí: se resuelve abajo con `t`, que en la firma
  // todavía no existe. Escrito a mano en inglés, el modal de diferir mostraba
  // "Select date" en medio de una pantalla en español, y cambiar el idioma de
  // la app no lo movía.
  placeholder,
  showTime = false,
  disabled = false,
  className,
  minDate,
  maxDate,
  isDateDisabled,
  redDates,
  status,
  "data-testid": testId,
}) {
  const { t } = useTranslation();
  const textoVacio = placeholder ?? t(showTime ? "common.selectDateTime" : "common.selectDate");
  const [open, setOpen] = React.useState(false);
  const [view, setView] = React.useState("days");

  // Stable identity so react-day-picker's Calendar doesn't treat the prop as
  // a new function every render and re-run per-cell. Critical when changing
  // months: without this, every cell of the visible month re-renders even
  // though min/max/isDateDisabled inputs didn't change.
  const calendarDisabled = React.useCallback(
    (date) => {
      if (minDate && date < new Date(minDate)) return true;
      if (maxDate && date > new Date(maxDate)) return true;
      if (isDateDisabled && isDateDisabled(date)) return true;
      return false;
    },
    [minDate, maxDate, isDateDisabled]
  );
  const {
    selectedDate,
    setSelectedDate,
    selectedTime,
    setSelectedTime,
    calMonth,
    setCalMonth,
    displayValue,
    hydrateFromValue,
  } = useDateTimeValue(value, showTime);

  const drilldownCtx = React.useMemo(
    () => ({ setView, calMonth, setCalMonth }),
    [calMonth, setCalMonth]
  );

  const handleOpenChange = (o) => {
    setOpen(o);
    if (!o) setView("days");
  };

  const handleSelect = (date) => {
    setSelectedDate(date);
    if (date) setCalMonth(date);
    if (!showTime && date) {
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const day = date.getDate().toString().padStart(2, "0");
      onChange(`${year}-${month}-${day}`);
      setOpen(false);
    }
  };

  const handleApply = () => {
    if (selectedDate) {
      const year = selectedDate.getFullYear();
      const month = (selectedDate.getMonth() + 1).toString().padStart(2, "0");
      const day = selectedDate.getDate().toString().padStart(2, "0");

      if (showTime && selectedTime) {
        const [hours, mins] = selectedTime.split(":");
        onChange(`${year}-${month}-${day}T${hours}:${mins}`);
      } else {
        onChange(`${year}-${month}-${day}`);
      }
    }
    setOpen(false);
  };

  const handleClear = () => {
    setSelectedDate(null);
    setSelectedTime(DEFAULT_TIME);
    onChange("");
    setOpen(false);
  };

  const handleCancel = () => {
    hydrateFromValue();
    setOpen(false);
  };

  const borderClass =
    status === "expired"
      ? "border-red-500 hover:border-red-400"
      : status === "expiring"
        ? "border-yellow-500 hover:border-yellow-400"
        : status === "valid"
          ? "border-green-500 hover:border-green-400"
          : "border-slate-700 hover:border-slate-600";

  return (
    <div className="relative">
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={testId}
            disabled={disabled}
            className={cn(
              "w-full flex items-center gap-2 bg-[#0f172a] border text-left rounded-xl px-4 py-3 text-sm transition-colors duration-200",
              borderClass,
              "focus:border-[#0052FF] focus:ring-1 focus:ring-[#0052FF] focus:outline-none",
              "disabled:opacity-50 disabled:cursor-not-allowed",
              !displayValue && "text-slate-500",
              displayValue && "text-white",
              className
            )}
          >
            <CalendarIcon className="w-4 h-4 text-slate-500 flex-shrink-0" />
            <span className="flex-1 truncate">{displayValue || textoVacio}</span>
            {showTime && <Clock className="w-3.5 h-3.5 text-slate-600" />}
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="p-0 bg-[#1e293b] border-slate-700 shadow-xl z-[100000] flex flex-col"
          style={{ width: 280, maxHeight: "var(--radix-popper-available-height)" }}
          align="start"
          side="bottom"
          sideOffset={8}
          avoidCollisions={true}
          collisionPadding={16}
        >
          <DrilldownCtx.Provider value={drilldownCtx}>
            <div className="overflow-y-auto">
              {view === "days" && (
                <div className="p-3 space-y-2">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={handleSelect}
                    month={calMonth}
                    onMonthChange={setCalMonth}
                    disabled={calendarDisabled}
                    modifiers={
                      redDates
                        ? {
                            red: (date) => {
                              const y = date.getFullYear();
                              const m = String(date.getMonth() + 1).padStart(2, "0");
                              const d = String(date.getDate()).padStart(2, "0");
                              return redDates.has(`${y}-${m}-${d}`);
                            },
                          }
                        : undefined
                    }
                    modifiersClassNames={
                      redDates
                        ? {
                            red: "!bg-red-500/20 !text-red-400 hover:!bg-red-500/30",
                          }
                        : undefined
                    }
                    initialFocus
                    className="p-0"
                    components={{ Caption: DrilldownCaption }}
                    classNames={calendarClassNames}
                  />

                  {showTime && (
                    <div className="border-t border-slate-700 pt-3 flex flex-col gap-2">
                      <span className="flex items-center gap-1.5 text-xs font-medium text-slate-400">
                        <Clock className="w-3.5 h-3.5" />
                        {t("dateTimePicker.time")}
                      </span>
                      <TimePickerSpinner
                        value={selectedTime}
                        onChange={(next) => setSelectedTime(next || "00:00")}
                      />
                    </div>
                  )}
                </div>
              )}

              {view === "months" && (
                <MonthPicker
                  calMonth={calMonth}
                  setCalMonth={setCalMonth}
                  setView={setView}
                  selectedDate={selectedDate}
                />
              )}

              {view === "years" && (
                <YearPicker
                  calMonth={calMonth}
                  setCalMonth={setCalMonth}
                  setView={setView}
                  selectedDate={selectedDate}
                />
              )}
            </div>

            <div className="flex items-center gap-2 border-t border-slate-700 px-3 py-2.5 flex-shrink-0 bg-[#1e293b]">
              <button
                type="button"
                onClick={handleClear}
                className="px-3 py-2 text-xs font-medium text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
              >
                {t("dateTimePicker.clear")}
              </button>
              <div className="flex-1" />
              {showTime ? (
                <>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="px-3 py-2 text-xs font-medium text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors"
                  >
                    {t("dateTimePicker.cancel")}
                  </button>
                  <button
                    type="button"
                    onClick={handleApply}
                    disabled={!selectedDate}
                    className="px-4 py-2 text-xs font-semibold text-white bg-altiv-navy hover:bg-altiv-navy-hover rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                  >
                    <Check className="w-3.5 h-3.5" />
                    {t("dateTimePicker.apply")}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="px-3 py-2 text-xs font-medium text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors"
                >
                  {t("dateTimePicker.close")}
                </button>
              )}
            </div>
          </DrilldownCtx.Provider>
        </PopoverContent>
      </Popover>
      {status && (
        <span
          className={cn(
            "absolute -top-1 -right-1 w-3 h-3 rounded-full",
            status === "expired" && "bg-red-500",
            status === "expiring" && "bg-yellow-500",
            status === "valid" && "bg-green-500"
          )}
        />
      )}
    </div>
  );
}

function DatePicker({
  value,
  onChange,
  // Sin texto por defecto: lo resuelve `DateTimePicker` con el idioma activo.
  placeholder,
  disabled = false,
  className,
  minDate,
  maxDate,
  isDateDisabled,
  redDates,
  status,
  "data-testid": testId,
}) {
  return (
    <DateTimePicker
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      showTime={false}
      disabled={disabled}
      className={className}
      minDate={minDate}
      maxDate={maxDate}
      isDateDisabled={isDateDisabled}
      redDates={redDates}
      status={status}
      data-testid={testId}
    />
  );
}

export { DateTimePicker, DatePicker, DEFAULT_TIME, parseValue };
